import { GROUPS, SET_GROUP, groupsFor, isSetter, positionLabel, sortRoster } from "@/lib/dvStats";
import { useT } from "@/lib/i18n";
import Counter from "./Counter";

const cell = "flex items-center justify-center";

export default function DesktopGrid({ match, stats, flash, flashRow, readOnly, bump, t }) {
  const { lang } = useT();
  return (
    <div className="hidden lg:block rounded-2xl border border-slate-200 bg-white shadow-soft overflow-hidden" data-testid="dv-desktop-grid">
      <div className="dv-grid items-stretch">
        <div className="row-span-2 flex items-end px-3 pb-2 border-b border-slate-200">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 whitespace-nowrap">
            # · {t("dv_player")}
          </span>
        </div>
        {GROUPS.map((g) => (
          <div key={g.category} className="px-1 pt-2 pb-1" style={{ gridColumn: `span ${g.types.length}` }}>
            <div className={`rounded-xl py-2 text-center font-heading font-black text-xs xl:text-sm tracking-widest ${g.header}`}>
              {t(g.labelKey)}
            </div>
          </div>
        ))}
        <div className="hidden xl:block px-1 pt-2 pb-1" style={{ gridColumn: "span 2" }}>
          <div className="rounded-xl py-2 text-center font-heading font-black text-xs tracking-wide bg-slate-200 text-slate-800">
            {t("dv_pct")}
          </div>
        </div>

        {GROUPS.map((g) =>
          g.types.map((ty) => (
            <div key={`${g.category}_${ty.type}`} className={`${g.band} ${cell} text-[11px] xl:text-xs font-bold text-slate-700 py-1.5 border-b border-slate-200`}>
              {t(ty.labelKey)}
            </div>
          ))
        )}
        <div className={`hidden xl:flex ${cell} bg-slate-100 text-[11px] font-bold text-slate-700 py-1.5 border-b border-slate-200`}>{t("dv_rec_pct")}</div>
        <div className={`hidden xl:flex ${cell} bg-slate-100 text-[11px] font-bold text-slate-700 py-1.5 border-b border-slate-200`}>{t("dv_att_pct")}</div>

        {sortRoster(match.roster).map((p, i, arr) => {
          const s = stats.players[p.player_id];
          const rowCls = flashRow === p.player_id ? "dv-row-flash" : "";
          const firstSetter = isSetter(p) && !(i > 0 && isSetter(arr[i - 1]));
          return [
            ...(firstSetter ? [
              <div key="set-hdr-n" className="flex items-center px-3 py-1 border-b border-slate-200 bg-emerald-50/60" data-testid="dv-set-header-row">
                <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700">{positionLabel("Πασαδόρος", lang)}</span>
              </div>,
              ...SET_GROUP.types.map((ty) => (
                <div key={`set-hdr-${ty.type}`} className={`${SET_GROUP.band} ${cell} flex-col py-1 border-b border-slate-200`}>
                  <span className={`rounded-md px-1.5 text-[9px] font-heading font-black tracking-widest ${SET_GROUP.header}`}>{t("dv_set")}</span>
                  <span className="text-[11px] xl:text-xs font-bold text-slate-700">{t(ty.labelKey)}</span>
                </div>
              )),
              ...GROUPS.slice(1).flatMap((g) => g.types.map((ty) => <div key={`set-hdr-${g.category}-${ty.type}`} className={`${g.band} border-b border-slate-200`} />)),
              <div key="set-hdr-r" className="hidden xl:block bg-slate-50 border-b border-slate-200" />,
              <div key="set-hdr-a" className="hidden xl:block bg-slate-50 border-b border-slate-200" />,
            ] : []),
            <div key={`${p.player_id}-n`} className={`${rowCls} flex items-center gap-2 px-3 py-1.5 border-b border-slate-100 min-w-0`} data-testid={`dv-row-${p.number}`}>
              <span className="font-heading font-black text-xl text-slate-900 w-7 shrink-0">{p.number}</span>
              <div className="min-w-0">
                <p className="font-bold text-slate-900 leading-tight truncate">{p.name}</p>
                <p className="text-[11px] text-slate-500 truncate">{positionLabel(p.position, lang)}</p>
                <p className="xl:hidden text-[11px] font-bold text-slate-700 whitespace-nowrap">
                  {isSetter(p) ? <span className="text-emerald-700">{t("dv_set_label")} {s.setPct}%</span> : <span className="text-emerald-700">{s.receptionPct}%</span>} · <span className="text-rose-700">{s.attackPct}%</span>
                </p>
              </div>
            </div>,
            ...groupsFor(p).flatMap((g) =>
              g.types.map((ty) => {
                const key = `${g.category}_${ty.type}`;
                return (
                  <div key={`${p.player_id}-${key}`} className={`${g.band} ${rowCls} ${cell} px-0.5 py-1.5 border-b border-slate-100 min-w-0`}>
                    <Counter
                      value={s[key]}
                      group={g}
                      disabled={readOnly}
                      flash={flash === `${p.player_id}_${key}`}
                      onPlus={() => bump(p.player_id, g.category, ty.type, 1)}
                      onMinus={() => bump(p.player_id, g.category, ty.type, -1)}
                      testid={`dv-counter-${p.number}-${key}`}
                    />
                  </div>
                );
              })
            ),
            <div key={`${p.player_id}-r`} className={`hidden xl:flex ${cell} bg-slate-50 font-heading font-black text-lg text-slate-800 border-b border-slate-100`} data-testid={`dv-recpct-${p.number}`}>
              {isSetter(p) ? <span className="flex flex-col items-center leading-none"><span className="text-[9px] font-bold uppercase text-emerald-700">{t("dv_set_label")}</span>{s.setPct}%</span> : `${s.receptionPct}%`}
            </div>,
            <div key={`${p.player_id}-a`} className={`hidden xl:flex ${cell} bg-slate-50 font-heading font-black text-lg text-slate-800 border-b border-slate-100`} data-testid={`dv-attpct-${p.number}`}>
              {s.attackPct}%
            </div>,
          ];
        })}

        <div className="flex items-center bg-slate-100 px-3 py-3 font-heading font-black text-xs xl:text-sm tracking-widest text-slate-800" data-testid="dv-team-row">
          {t("dv_team_totals")}
        </div>
        {GROUPS.map((g) =>
          g.types.map((ty) => (
            <div key={`t-${g.category}_${ty.type}`} className={`${g.band} ${cell} font-heading font-black text-xl text-slate-900 py-3`}>
              {stats.team[`${g.category}_${ty.type}`]}
            </div>
          ))
        )}
        <div className={`hidden xl:flex ${cell} bg-slate-100 font-heading font-black text-xl text-slate-900`} data-testid="dv-team-recpct">
          {stats.team.receptionPct}%
        </div>
        <div className={`hidden xl:flex ${cell} bg-slate-100 font-heading font-black text-xl text-slate-900`} data-testid="dv-team-attpct">
          {stats.team.attackPct}%
        </div>
      </div>
    </div>
  );
}
